import { useTheme } from '@mui/material';

import type { IAccordionData } from '@rumblefish/ui/src/components/Rumblefish23Theme/Accordion/CustomizedAccordion.types';

export const faqData = {
  mainText: `FAQ`,
  description: `Mobile development services with Rumble Fish`,
};

export const AccordionData = (): IAccordionData[] => {
  const theme = useTheme();
  return [
    {
      title: 'Q: What is mobile development?',
      description: `
        A: Mobile development is the process of creating software applications 
        that run on mobile devices, such as smartphones and tablets. This involves 
        writing code and developing functionalities for mobile platforms like 
        iOS (Apple’s operating system) and Android (Google’s operating system).
      `,
      gradientOnFocus: theme.mixins.greenGradient?.background as string,
    },

    {
      title:
        'Q: Is it possible to develop apps for both iOS and Android simultaneously?',
      description: `
        A: Yes! It is possible to develop apps for both iOS and Android at the same 
        time using cross-platform development tech stack, like React Native. Mobile developers 
        write code once and deploy it on both platforms, significantly reducing development 
        time and costs while maintaining a consistent look and feel across devices.
       `,
      gradientOnFocus: theme.mixins.blueGradient?.background as string,
    },
    {
      title:
        'Q: What type of mobile development services does Rumble Fish offer?',
      description: `
        A: At Rumble Fish, we offer a comprehensive suite of mobile development services 
        designed to meet a wide range of business needs and deliver exceptional 
        user experiences. Here are the key mobile development services we provide:
        <br />
        <ul>
          <li>
            <b>iOS Native App Development</b>
            <br />
            Our team specializes in developing high-quality native apps for Apple's iOS platform. 
            Utilizing battle-tested technology stack, we create apps that are optimized for performance, 
            security, and user experience. From concept to deployment, we ensure that your app 
            adheres to Apple’s stringent guidelines and leverages the latest iOS features.
          </li>
          <li>
            <b>Android Native App Development</b>
            <br />
            We build robust and scalable native apps for the Android platform. Our Android 
            development services focus on creating apps that are visually appealing, highly 
            functional, and secure. We ensure compatibility across a wide range of Android 
            devices and OS versions, delivering a seamless user experience.
          </li>
          <li>
            <b>Cross-Platform App Development</b>
            <br />
            For clients looking to reach both iOS and Android users without maintaining separate codebases, 
            we offer cross-platform app development using React Native. This approach allows us to deliver 
            cost-effective solutions that maintain a native look and feel across platforms.
          </li>
          <li>
            <b>Mobile UI/UX Design Services</b>
            <br />
            We believe that great design is at the heart of every successful app. Our UI/UX design 
            services focus on creating intuitive, engaging, and aesthetically pleasing interfaces. 
            From wireframing and prototyping to user testing, we ensure that the design meets 
            user needs and enhances overall app usability.
          </li>
        </ul>
      `,
      gradientOnFocus: theme.mixins.violetGradient?.background as string,
    },
    {
      title: 'Q: Why hire Rumble Fish for mobile app development?',
      description: `
        A: Hiring Rumble Fish for mobile app development offers numerous benefits 
        that can enhance your project's success: 
        <br />
        <br />
        Key features of decentralized finance are:
        <ul>
          <li>
            <b>Expertise and Experience: </b>
            With years of experience in the industry, Rumble Fish boasts a team of seasoned 
            professionals who excel in mobile app development. Our developers and designers are 
            experts in their respective fields, ensuring that your project is in capable hands.
          </li>
          <li>
            <b>Customized Solutions: </b>
            We understand that every business is unique, which is why we take a tailored approach 
            to your mobile app development. Whether you're a startup or a Fortune 500 company, 
            we work closely with you to understand your goals and deliver custom solutions 
            that meet your specific needs.
          </li>
          <li>
            <b>Cutting-Edge Technologies: </b>
            At Rumble Fish, we stay ahead of the curve by embracing the latest technologies and 
            trends in mobile app development. From AI to blockchain, we leverage innovative 
            tools and frameworks to create apps that stand out in today's competitive landscape.
          </li>
          <li>
            <b>Focus on User Experience: </b>
            Our UI/UX design team is dedicated to creating visually stunning and intuitive 
            interfaces that enhance user engagement and satisfaction. We prioritize user feedback 
            and usability testing to ensure that your app resonates with your target audience.
          </li>
          <li>
            <b>Quality Assurance: </b>
            Quality is at the forefront of everything we do. Our rigorous testing processes and 
            QA protocols ensure that your app is free from bugs, glitches, and performance 
            issues. We strive for perfection in every aspect of development, from code quality 
            to user experience.
          </li>
          <li>
            <b>Transparent Communication: </b>
            Communication is key to a successful partnership. We keep you informed and involved 
            at every stage of the development process, providing regular updates, feedback 
            sessions, and transparent reporting.
          </li>
          <li>
            <b>Post-Launch Support: </b>
            Our commitment to your success doesn't end with the launch of your app. We provide 
            ongoing maintenance and support services to address any issues, implement updates, 
            and ensure that your app continues to perform optimally in the long run.
          </li>
          <li>
            <b>Proven Track Record: </b>
            With a portfolio of successful projects and satisfied clients, Rumble Fish has earned 
            a reputation for excellence in mobile app development. Our track record speaks for 
            itself, showcasing our ability to deliver high-quality solutions that exceed 
            expectations. Try us!
          </li>
        </ul>
        Partner with Rumble Fish for mobile app development and let us bring your ideas to life and 
        help you achieve your business objectives in the rapidly evolving world of mobile technology.
      `,
      gradientOnFocus: theme.mixins.purpleGradient?.background as string,
    },
  ];
};
